import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, Receipt, MessageSquare,
  Brain, FileText, Settings, ScrollText, Menu, LogOut, ChevronDown,
  HelpCircle, KeyRound, MapPin, BookOpen, UploadCloud, Settings as SettingsIcon,
  ShieldAlert,
} from "lucide-react";
import clsx from "clsx";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import BrandLogo from "@/components/BrandLogo";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/campaigns", label: "Campaigns", icon: Briefcase, testid: "nav-campaigns" },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "nav-billing" },
  { to: "/ledger", label: "Ledger", icon: BookOpen, testid: "nav-ledger" },
  { to: "/reviews", label: "Reviews", icon: MessageSquare, testid: "nav-reviews" },
  { to: "/intelligence", label: "Intelligence", icon: Brain, testid: "nav-intelligence" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/branches", label: "Branches", icon: MapPin, testid: "nav-branches", roles: ["crm_manager", "branch_manager"] },
  { to: "/deletion-requests", label: "Requests", icon: ShieldAlert, testid: "nav-deletion-requests", roles: ["crm_manager", "branch_manager"] },
  { to: "/bulk-import", label: "Bulk Import", icon: UploadCloud, testid: "nav-bulk-import", roles: ["crm_manager", "accounts"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", roles: ["crm_manager"] },
  { to: "/admin", label: "Admin", icon: Settings, testid: "nav-admin", roles: ["crm_manager"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit", roles: ["crm_manager"] },
  { to: "/help", label: "Help", icon: HelpCircle, testid: "nav-help" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [adverseCount, setAdverseCount] = useState(0);

  const visibleNav = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  React.useEffect(() => {
    if (user?.role !== "crm_manager") return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const { data } = await (await import("@/lib/api")).default.get("/deletion-requests/pending-count");
        if (!cancelled) setPendingCount(data.count || 0);
      } catch {}
    };
    fetchPending();
    const t = setInterval(fetchPending, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.role]);

  React.useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const fetchAdverse = async () => {
      try {
        const { data } = await (await import("@/lib/api")).default.get("/dashboard/unread-adverse-count");
        if (!cancelled) setAdverseCount(data.count || 0);
      } catch {}
    };
    fetchAdverse();
    const t = setInterval(fetchAdverse, 60000);
    // refresh after mark-seen events fired by Dashboard
    const onSeen = () => setAdverseCount(0);
    window.addEventListener("mptg:adverse-seen", onSeen);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("mptg:adverse-seen", onSeen);
    };
  }, [user?.id]);

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={clsx(
          "mptg-sidebar fixed md:static z-40 w-64 h-screen flex flex-col transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2">
          <BrandLogo size={36} fallbackChar="M" tone="dark" />
          <div>
            <div className="font-heading font-bold tracking-tight text-white text-lg leading-none">MPTG-CRM</div>
            <div className="text-[11px] text-slate-400 mt-1 tracking-wider uppercase">MAS Callnet India</div>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const showBadge = n.to === "/deletion-requests" && user?.role === "crm_manager" && pendingCount > 0;
            const showAdverse = n.to === "/" && adverseCount > 0;
            const inner = (
              <>
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1">{n.label}</span>
                {showBadge && (
                  <span
                    className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[#C0392B] text-white"
                    data-testid="nav-deletion-requests-badge"
                  >
                    {pendingCount}
                  </span>
                )}
                {showAdverse && (
                  <span
                    className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[#C0392B] text-white animate-pulse"
                    data-testid="nav-adverse-badge"
                    title={`${adverseCount} new adverse event${adverseCount === 1 ? "" : "s"} in your portfolio`}
                  >
                    {adverseCount > 99 ? "99+" : adverseCount}
                  </span>
                )}
                {n.tag && (
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 bg-white/5 px-1.5 py-0.5 rounded">
                    {n.tag}
                  </span>
                )}
              </>
            );
            if (n.disabled) {
              return (
                <div
                  key={n.to}
                  className="mptg-sidebar-item flex items-center gap-3 px-5 py-2.5 text-sm opacity-50 cursor-not-allowed"
                  data-testid={n.testid}
                >
                  {inner}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  clsx("mptg-sidebar-item flex items-center gap-3 px-5 py-2.5 text-sm", isActive && "active")
                }
              >
                {inner}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-slate-400">
          v1.0 · Phase 1–4 MVP
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 border-b border-slate-200 flex items-center justify-between px-4 md:px-6 bg-white sticky top-0 z-30" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 hover:bg-slate-100 rounded"
              onClick={() => setOpen(!open)}
              data-testid="sidebar-toggle"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="font-heading font-semibold text-slate-800 text-base tracking-tight">
              MPTG-CRM
            </div>
          </div>
          <div className="relative">
            <button
              className="flex items-center gap-2 px-3 py-1.5 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="user-menu-btn"
            >
              <div className="w-7 h-7 rounded-full bg-[#2E86C1] text-white flex items-center justify-center text-xs font-semibold">
                {user?.name?.split(" ").map((x) => x[0]).slice(0, 2).join("") || "U"}
              </div>
              <div className="text-left hidden sm:block">
                <div className="text-xs font-semibold leading-tight" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{ROLE_LABELS[user?.role] || user?.role}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg py-1 z-50" data-testid="user-menu-dropdown">
                <div className="px-3 py-2 border-b border-slate-100">
                  <div className="text-xs text-slate-500">Signed in as</div>
                  <div className="text-sm font-medium truncate">{user?.email}</div>
                </div>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={() => { setMenuOpen(false); setPwdOpen(true); }}
                  data-testid="change-password-btn"
                >
                  <KeyRound className="w-4 h-4" /> Change password
                </button>
                <button
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-[#F8FAFC]" data-testid="main-content">
          <Outlet />
        </main>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={() => setOpen(false)} />
      )}

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
